import React, { useState } from 'react';
import { addComment, addCommentByToken, uploadAttachmentToBlob, updateTicket, fetchEmailTemplates, sendEmail } from './api';
import { useUser } from '../context/UserContext';
import QuillEditor from './shared/QuillEditor';

const TicketDetailModal = ({ isOpen, onClose, ticket, onTicketUpdated }) => {
  const { user } = useUser();
  
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Track status-update spinner
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Attachment states
  const [attachments, setAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // For guest commenters, capture their e-mail address
  const [guestEmail, setGuestEmail] = useState('');


  // Attachment handling functions
  const handleFileSelect = (files) => {
    const newAttachments = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    // Use content from reusable editor
    const comment = newComment;
    
    // Require at least comment or attachment
    if (!comment.trim() && attachments.length === 0) {
      setError("Please enter a comment or add an attachment.");
      return;
    }

    // For guest flow make sure e-mail provided
    if ((!user || !user.email) && !guestEmail.trim()) {
      setError("Please provide your email address.");
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Build base comment payload
      const commentData = {
        ticketId: ticket.id,
        userId: user?.id || null,
        userEmail: user?.email || guestEmail.trim() || null,
        comment: comment.trim() || null,
        isInternal: false
      };
      
      // If there are attachments, upload them first
      if (attachments.length > 0) {
        const uploaded = await Promise.all(
          attachments.map(att => uploadAttachmentToBlob(att.file, 'data-dumps'))
        );

        commentData.has_attachment = 1;
        commentData.attachmentName = attachments.map(a => a.name).join(', ');
        commentData.attachmentPath = uploaded.map(u => u.path).join(', ');
        commentData.attachmentSize = attachments.reduce((total, a) => total + a.size, 0);
        commentData.attachmentMimeType = attachments[0].type;
      }
      
      let addedComment;
      if (ticket.guest_token && (!user || !user.email)) {
        // Guest flow
        addedComment = await addCommentByToken(ticket.guest_token, commentData);
      } else {
        // Authenticated flow
        addedComment = await addComment(ticket.id, commentData);
      }
      
              // Update the ticket with the new comment
        if (onTicketUpdated) {
          onTicketUpdated({
            ...ticket,
            comments: [...(ticket.comments || []), addedComment]
          });
        }

      // ────────────────────────────────────────────────
      // Notify mentioned users via e-mail
      // ────────────────────────────────────────────────
      try {
        const templates = await fetchEmailTemplates();

        const ticketUrl = `${window.location.origin}/ticket/${ticket.id}`;
        const guestUrl = ticket.guest_token
          ? `${window.location.origin}/guest/ticket/${ticket.guest_token}`
          : null;

        const interpolate = (tpl = '') => {
          const html = typeof tpl === 'string' ? tpl : tpl?.html || '';
          return html
            .replace(/{{\s*ticketId\s*}}/gi, ticket.id)
            .replace(/{{\s*ticketUrl\s*}}/gi, ticketUrl)
            .replace(/{{\s*guestUrl\s*}}/gi, guestUrl || '');
        };

        const extractMentionEmails = (html) => {
          const div = document.createElement('div');
          div.innerHTML = html || '';
          const mentionEls = div.querySelectorAll('.ql-mention, .mention');
          return Array.from(
            new Set(
              Array.from(mentionEls)
                .map((el) => el.dataset.id || el.dataset.value || el.innerText || '')
                .filter((e) => e && e.includes('@'))
            )
          );
        };

        const mentionEmails = extractMentionEmails(commentData.comment || '');

        const emailPromises = mentionEmails
          .filter((email) => email && email !== (user?.email || guestEmail))
          .map((email) =>
            sendEmail({
              to: email,
              from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
              subject: `You were mentioned in Ticket ${ticket.id}`,
              html: interpolate(
                templates.ticket_mentioned ||
                  `<p>You were mentioned in Ticket <a href="${ticketUrl}">#${ticket.id}</a>.</p>`
              ),
            })
          );

        await Promise.all(emailPromises);
      } catch (mailErr) {
        console.error('Failed to send mention notification e-mails:', mailErr);
      }
 
      // Clear editor & guest email
      setNewComment('');
      setAttachments([]);
      if (!user?.email) setGuestEmail('');
      
    } catch (err) {
      console.error("Error adding comment:", err);
      setError("Failed to add comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-yellow-100 text-yellow-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Canceled': return 'bg-red-100 text-red-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Removed custom Quill lifecycle hooks

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const colorPalette = [
    'bg-red-500',
    'bg-green-500',
    'bg-blue-500',
    'bg-purple-500',
    'bg-yellow-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
  ];

  const getUserColor = (email) => {
    if (!email) return 'bg-gray-400';
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colorPalette[Math.abs(hash) % colorPalette.length];
  };

  const tagColors = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0ea5e9', '#16a34a', '#9333ea', '#ea580c', '#6b7280'];
  const getTagColor = (tag) => {
    if (!tag) return '#6b7280';
    let hash = 0;
    for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    return tagColors[Math.abs(hash) % tagColors.length];
  };

  // ────────────────────────────────────────────────
  // Change ticket status helper
  // ────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    if (!ticket || ticket.status === newStatus) return;
    try {
      setIsUpdatingStatus(true);
      const previousStatus = ticket.status;
      const updated = await updateTicket(ticket.id, {
        status: newStatus,
        updatedBy: user?.email || null
      });

      // Create a timeline comment for the status change
      let statusChangeComment;
      try {
        statusChangeComment = await addComment(ticket.id, {
          userId: user?.id ?? 0,
          userEmail: user?.email || null,
          comment: `<p>Status changed from ${previousStatus} to ${newStatus}${user?.email ? ` by ${user.email}` : ''}.</p>`,
          isInternal: false
        });
      } catch (commentErr) {
        // Fallback local comment to reflect change in UI even if API fails
        statusChangeComment = {
          user_email: user?.email || 'system',
          comment: `<p>Status changed from ${previousStatus} to ${newStatus}${user?.email ? ` by ${user.email}` : ''}.</p>`,
          created_at: new Date().toISOString(),
          is_internal: 0,
          has_attachment: 0
        };
      }

      onTicketUpdated?.({
        ...updated,
        comments: [
          ...((updated && updated.comments) || ticket.comments || []),
          statusChangeComment,
        ],
      });
    } catch (err) {
      console.error('Failed to update ticket status:', err);
      setError(err.message || 'Failed to update ticket status.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (!isOpen || !ticket) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[100] overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="sticky top-0 z-10 flex justify-between items-center mb-4 bg-white px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Ticket Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl leading-none"
            aria-label="Close ticket details"
          >
            ✕
          </button>
        </div>

        <div className="px-6 pb-6">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Ticket Header */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{ticket.title}</h3>
              <p className="text-sm text-gray-600">ID: {ticket.id}</p>
            </div>
            <div className="flex space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                {ticket.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Country:</span>
              <p className="text-gray-600">{ticket.country}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Level:</span>
              <p className="text-gray-600">{ticket.level}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Store:</span>
              <p className="text-gray-600">{ticket.store}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Product:</span>
              <p className="text-gray-600">{ticket.antigen_name}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Created:</span>
              <p className="text-gray-600">{new Date(ticket.created_at).toLocaleDateString()}</p>
            </div>
            {(() => {
              const isErtCustom = String(ticket.priority || '') === 'Custom' && String(ticket.country_code || ticket.country || '').toUpperCase() === 'ERT';
              const customList = (() => {
                const cn = ticket.custom_notify;
                if (Array.isArray(cn)) return cn;
                if (typeof cn === 'string') {
                  try {
                    const parsed = JSON.parse(cn);
                    return Array.isArray(parsed) ? parsed : [];
                  } catch (_) { return []; }
                }
                return [];
              })();
              if (isErtCustom && customList.length > 0) {
                return (
                  <div className="md:col-span-3">
                    <span className="font-medium text-gray-700">Recipients (Custom):</span>
                    <div className="mt-1 text-gray-600 text-sm">
                      {customList.map((r, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span>{r.email}</span>
                          <span className="text-gray-400">•</span>
                          <span>{r.organization}</span>
                          <span className="text-gray-400">•</span>
                          <span>+{(Number.isFinite(Number(r?.days)) ? Number(r.days) : 0)}d</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div>
                  <span className="font-medium text-gray-700">Assigned To:</span>
                  <p className="text-gray-600">{ticket.assigned_to_email}</p>
                </div>
              );
            })()}
            {/* Newly added info blocks */}
            <div>
              <span className="font-medium text-gray-700">Store ID (LMS):</span>
              <p className="text-gray-600">{ticket.lms_id}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Parent Store:</span>
              <p className="text-gray-600">{ticket.parent_store}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Root Cause:</span>
              <p className="text-gray-600">{ticket.root_cause}</p>
            </div>
            {ticket.org_unit_tags && String(ticket.org_unit_tags).trim() && (
              <div className="md:col-span-3">
                <span className="font-medium text-gray-700">Org Unit Tags:</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {String(ticket.org_unit_tags)
                    .split(/[;,]+/)
                    .map(t => t.trim())
                    .filter(Boolean)
                    .map((t) => (
                      <span key={t} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: getTagColor(t), color: 'white' }}>
                        {t}
                      </span>
                    ))}
                </div>
              </div>
            )}
            {ticket.oversight && (
              <div>
                <span className="font-medium text-gray-700">Oversight:</span>
                <p className="text-gray-600">{ticket.oversight}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Description */}
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-900 mb-2">Description</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center space-x-2">
                {ticket.creator_email && (
                  <span className="font-medium text-gray-900">{ticket.creator_email}</span>
                )}
              </div>
              <span className="text-sm text-gray-500">
                {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : ''}
              </span>
            </div>
            <div 
              className="text-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: ticket.description }}
            />
          </div>
        </div>

        {/* Ticket Attachments */}
        {ticket.has_attachment && ticket.attachment_path && (
          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-900 mb-2">Attachments</h4>
            <ul className="list-disc pl-5 text-sm">
              {ticket.attachment_path.split(',').map((path, idx) => {
                const names = ticket.attachment_name ? ticket.attachment_name.split(',') : [];
                const name = names[idx] ? names[idx].trim() : `File ${idx + 1}`;
                return (
                  <li key={idx} className="mb-1">
                    <a
                      href={path.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {name}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        
        {/* Escalations Section */}
        {ticket.escalations && ticket.escalations.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Escalations</h4>
            <div className="relative mb-4">
              <div className="border-l-2 border-gray-200">
                {ticket.escalations.map((e, idx) => (
                  <div key={idx} className="ml-4 mb-4 relative">
                    <span className={`absolute -left-2.5 top-3 w-4 h-4 ${e.type === 'tier3' ? 'bg-red-500' : e.type === 'reminder_tier1' ? 'bg-blue-500' : 'bg-yellow-500'} rounded-full`} />
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <div className="font-medium text-gray-900 capitalize">
                          {e.type === 'tier3'
                            ? 'Tier 3 Escalation'
                            : e.type === 'reminder_tier1'
                              ? 'Tier 1 Reminder (Every 2 Days)'
                              : 'Tier 2 Escalation'}
                        </div>
                        <span className="text-xs text-gray-500">{e.occurredAt ? new Date(e.occurredAt).toLocaleString() : ''}</span>
                      </div>
                      {e.dueAt && (
                        <div className="text-xs text-gray-600 mb-1">Due at: {new Date(e.dueAt).toLocaleString()}</div>
                      )}
                      {e.type === 'reminder_tier1' && e.reminderCycle !== null && (
                        <div className="text-xs text-gray-600 mb-1">Reminder cycle: {e.reminderCycle}</div>
                      )}
                      <div className="text-xs text-gray-700">Emails sent to: {(e.sentTo || []).join(', ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Comments</h4>
          {/* Comments Timeline */}
          {ticket.comments && ticket.comments.length > 0 ? (
            <div className="relative mb-6">
              <div className="border-l-2 border-gray-200">
                {ticket.comments.map((comment, index) => (
                  <div key={index} className="ml-4 mb-6 relative">
                    <span className={`absolute -left-2.5 top-3 w-4 h-4 ${getUserColor(comment.user_email)} rounded-full`} />
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{comment.user_email}</span>
                          {!!comment.is_internal && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Internal</span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      {comment.comment && (
                        <div
                          className="text-gray-700 mb-2 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: comment.comment }}
                        />
                      )}
                      {!!comment.has_attachment && (
                        <div className="flex items-center space-x-2 text-sm text-blue-600">
                          <span>{comment.attachment_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4 mb-6">No comments yet</p>
          )}
          
          {/* Add Comment Form */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h5 className="font-medium text-gray-900 mb-3">Add Comment</h5>
            
                         <form onSubmit={handleSubmitComment}>
               {/* Guest e-mail field */}
               {!user?.email && (
                 <div className="mb-3">
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Your Email <span className="text-red-500">*</span>
                   </label>
                   <input
                     type="email"
                     value={guestEmail}
                     onChange={(e) => setGuestEmail(e.target.value)}
                     className="w-full p-2 border rounded-md"
                     placeholder="you@example.com"
                     required
                   />
                 </div>
               )}

               <div className="mb-3">
                 <div className="border rounded-md overflow-hidden">
                   <QuillEditor
                     value={newComment}
                     onChange={setNewComment}
                     placeholder="Add your comment here..."
                     height="100px"
                     autoGrow
                     resizable
                     minHeight="100px"
                     maxHeight="500px"
                   />
                 </div>
               </div>
              
              {/* Attachment Section */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments
                </label>
                
                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    isDragOver 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center">
                    <svg className="w-6 h-6 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop files here, or{' '}
                      <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
                        browse
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileSelect(e.target.files)}
                        />
                      </label>
                    </p>
                  
                  </div>
                </div>
                
                {/* Attachments List */}
                {attachments.length > 0 && (
                  <div className="mt-3">
                    <h6 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h6>
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="text-sm text-gray-700">{attachment.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(attachment.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            </form>
          </div>
        </div>
        
        {/* ─────────────────────────── Ticket Actions ─────────────────────────── */}
        <hr className="my-6" />
        <h4 className="text-lg font-medium text-gray-900 mb-3">Ticket Actions</h4>
        <div className="sticky bottom-0 -mx-6 flex items-center justify-between mt-2 bg-white px-6 py-4 border-t">
          {/* Left-aligned ticket status actions */}
          <div className="flex gap-2">
            {ticket.status !== 'Resolved' && (
              <button
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded text-sm disabled:opacity-50"
                disabled={isUpdatingStatus}
                onClick={() => handleStatusChange('Resolved')}
              >
                Resolve
              </button>
            )}
            {ticket.status !== 'Canceled' && (
              <button
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded text-sm disabled:opacity-50"
                disabled={isUpdatingStatus}
                onClick={() => handleStatusChange('Canceled')}
              >
                Cancel
              </button>
            )}
            {ticket.status !== 'Closed' && (
              <button
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded text-sm disabled:opacity-50"
                disabled={isUpdatingStatus}
                onClick={() => handleStatusChange('Closed')}
              >
                Close Ticket
              </button>
            )}
          </div>
          {/* Right-aligned modal close */}
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded text-sm"
          >
            Close
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailModal; 